/* eslint-disable @typescript-eslint/no-explicit-any,no-bitwise,no-plusplus */
import type { ParsedUrlQuery } from 'querystring';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import {
  createPlatformSearchId,
  createPlatformSearchIdFromPreClickId,
  getUIDPrefixFourChars,
} from '@/utils/searchIdUtils.ts';
import { LeisureType } from '@/domainLogic/common/enums.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import type { ProductsData } from '../../api/search/products/createProductsData.ts';
import { type DateItem, type Dates, DatesType, DateType, type SuggestionItem, TagType } from './utils';

export const PATTERN_LIMIT = 16;

export const noop = () => {};
export const noopPromise = () => Promise.resolve();

export type CodeType = { code: string | number; type: TagType };
export const getCodeTypeList = (suggestionItems: SuggestionItem | SuggestionItem[] | undefined): CodeType[] => {
  let res: CodeType[];

  if ((suggestionItems as SuggestionItem[]).length) {
    res = (suggestionItems as SuggestionItem[]).map((item) => ({ code: item.code, type: item.tagType }) as CodeType);
  } else if ((suggestionItems as SuggestionItem).code) {
    res = [
      {
        code: (suggestionItems as SuggestionItem).code,
        type: (suggestionItems as SuggestionItem).tagType || TagType.ANYWHERE,
      },
    ];
  } else {
    res = [{ code: 'A', type: TagType.ANYWHERE }];
  }

  return res;
};

export const getCodeList = (suggestionItem: SuggestionItem | SuggestionItem[] | undefined): string[] =>
  getCodeTypeList(suggestionItem).map((codeItem) => `${codeItem.code}`);

export const getPeople = (data: { adults: number; ages: number[] }[]) => {
  const adults = data.map((roomData) => roomData.adults).join(';');
  const hasAges = !!data.find((roomData) => roomData.ages.length > 0);
  const ages = hasAges ? data.map((roomData) => roomData.ages.join(',')).join(';') : '';
  return {
    adults,
    ages,
    hasAges,
  };
};

export const stringHash: (str: string) => number = (str): number => {
  let hash = 5381;
  let i = str.length;

  while (i) {
    hash = (hash * 33) ^ str.charCodeAt(--i);
  }

  return hash >>> 0;
};

export const getSearchHash = (searchParams: SearchParams): number =>
  stringHash(
    JSON.stringify({
      origin: searchParams[URLParameters.ORIGIN],
      destination: searchParams[URLParameters.DESTINATION],
      dateFrom: searchParams[URLParameters.DATE_FROM],
      dateTo: searchParams[URLParameters.DATE_TO],
      adults: searchParams[URLParameters.ADULTS],
      ages: searchParams[URLParameters.AGES],
      sort: searchParams[URLParameters.SORT],
      searchMode: searchParams[URLParameters.SEARCH_MODE],
    })
  );

export const GEO_DESTINATION_PREFIX = 'G-';
export const GEO_DESTINATION_SUB_TYPE = {
  CITY: 'CTY',
  COUNTRY: 'CNT',
  HOTEL: 'HTL',
  POINT_OF_INTEREST: 'POI',
  REGION: 'REG',
  TAG: 'TOR',
};

// COUNTRY sub-type IDs may be ISO alpha codes (e.g. CNT-MT) as well as numeric; every other
// sub-type is numeric-only.
const NUMERIC_GEO_SUB_TYPES = Object.values(GEO_DESTINATION_SUB_TYPE).filter(
  (subType) => subType !== GEO_DESTINATION_SUB_TYPE.COUNTRY
);
const GEO_SUB_TYPE_REGEX = new RegExp(
  `^(?:(?:${NUMERIC_GEO_SUB_TYPES.join('|')})-\\d+|${GEO_DESTINATION_SUB_TYPE.COUNTRY}-[A-Za-z0-9]+)$`
);
export const isGeoSubTypeDestination = (val: string): boolean => GEO_SUB_TYPE_REGEX.test(val);

export const isGeoCity = (id: string): boolean => id?.startsWith(`${GEO_DESTINATION_SUB_TYPE.CITY}-`);
export const isGeoTag = (id: string): boolean => id?.startsWith(`${GEO_DESTINATION_SUB_TYPE.TAG}-`);
export const isGeoRegion = (id: string): boolean => id?.startsWith(`${GEO_DESTINATION_SUB_TYPE.REGION}-`);

export const isGeoDestination = (val: string): boolean => isGeoSubTypeDestination(val ?? '');

export const isTag = (val: any) => {
  if (!val) return false;
  if (val.toString().match(/^\d+$/)) return false;
  return val.length > 2 && val.indexOf('MAPSEARCH') === -1 && !isGeoSubTypeDestination(val.toString());
};

export const trimTag = (val: any) => {
  if (!isTag(val)) return val;
  return decodeURIComponent(val.toString()).replace('TAG_', '');
};

// Whether a destination name names one place ("Tenerife") rather than a marketing list of them ("Mexico -
// Cancun, Riviera Maya") or a place with its area ("Cala d'Or, Majorca", "Agios Ioannis (Corfu)").
export const isSinglePlaceName = (name: string): boolean => !/\s[-–]\s|,|\(/.test(name);
export const isNumber = (value: any) => typeof value === 'number' && Number.isFinite(value);
export const isCity = (val: string) => val && val.toString().length > 2 && isNumber(val);

export const getLeisureTagType = (val: string): TagType => {
  switch (val) {
    case LeisureType.CITY_BREAKS:
      return TagType.CITY_BREAKS;
    case LeisureType.BEACH:
      return TagType.BEACH;
    case LeisureType.SKI:
      return TagType.SKI;
    case LeisureType.SPA:
      return TagType.SPA;
    case LeisureType.EXOTIC:
    default:
      return TagType.EXOTIC;
  }
};

export const isValidDate = (d: any) => d instanceof Date && !Number.isNaN(d.valueOf());

export const isString = (val: any): boolean => typeof val === 'string' || val instanceof String;

export const isObject = (val: any): boolean => typeof val === 'object' && val !== null && !Array.isArray(val);

export const convertDates = (dateFrom: string, dateTo: string, addDays: number): Dates => {
  const departureDate: DateItem = {
    type: DateType.EXACT,
  };
  if (isValidDate(new Date(dateFrom)) || (addDays && isValidDate(new Date(dateFrom.split(',')[0])))) {
    departureDate.date = dateFrom;
  }
  const returnDate: DateItem = {
    type: DateType.EXACT,
  };
  if (isValidDate(new Date(dateTo)) || (addDays && isValidDate(new Date(dateTo.split(',')[1])))) {
    returnDate.date = dateTo;
  }
  const flexDate: DateItem = {
    subType: DateType.TIME,
    type: DateType.ANYTIME,
    daysFrom: 2,
    daysTo: 3,
  };
  if (dateTo.indexOf('T') !== -1) {
    delete returnDate.date;
    flexDate.subType = DateType.TIME;
    flexDate.daysFrom = parseInt(dateTo.split(',')[1], 10);
    flexDate.daysTo = parseInt(dateTo.split(',')[2], 10);
  }
  if (dateTo.indexOf('W') !== -1) {
    flexDate.subType = DateType.WEEKEND;
  }
  if (dateFrom.indexOf('M,') !== -1) {
    delete departureDate.date;
    flexDate.type = DateType.MONTHS;
    flexDate.months = dateFrom.split('M,')[1].split(',');
  }
  if (dateFrom.indexOf('A') !== -1) {
    flexDate.type = DateType.ANYTIME;
  }

  return {
    departureDate,
    returnDate,
    flexDate,
    subDate: addDays,
    type: departureDate.date ? DatesType.CALENDAR : DatesType.FLEXIBLE,
  };
};

export const datesToQuerystring = (dates: Dates): { dateFrom: string; dateTo: string; adddays: number } => {
  let dateFrom = '';
  let dateTo = '';
  let adddays = 0;

  if (dates.type === DatesType.CALENDAR && dates.departureDate.date && dates.returnDate.date) {
    dateFrom = dates.departureDate.date;
    dateTo = dates.returnDate.date;
    if (dates.subDate !== 0) {
      adddays = dates.subDate;
    }
  } else {
    if (dates.flexDate.type === DateType.ANYTIME) {
      dateFrom = 'A';
    } else if (dates.flexDate.type === DateType.MONTHS) {
      dateFrom = `M,${dates?.flexDate?.months?.join(',')}`;
    }
    if (dates.flexDate.subType === DateType.WEEKEND) {
      dateTo = 'W';
    } else if (dates.flexDate.subType === DateType.TIME) {
      dateTo = `T,${dates.flexDate.daysFrom},${dates.flexDate.daysTo}`;
    }
    adddays = 0;
  }

  return { dateFrom, dateTo, adddays };
};

export const getLastDayOfMonth = ({ year, month }: { year: number; month: number }) =>
  new Date(year, month, 0).getDate();

export const getIsApp = (searchParams: SearchParams): boolean =>
  searchParams?.appview === 'true' || searchParams?.app === 'true';
export const getIsAppEnableWidget = (query?: ParsedUrlQuery): boolean => query?.appEnableWidget === 'true';

export const getColorFromCSSVariable = (cssVariableName: string): string =>
  canUseDOM ? getComputedStyle(document.documentElement)?.getPropertyValue(cssVariableName) : '';

export const hexToRGB = (hex: string, isFormatted?: boolean) => {
  // from #f2007d' to '242,0,125' or 'rgb(242,0,125)'
  let r = 0;
  let g = 0;
  let b = 0;

  // Handle 6 digits.
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  // Handle 3 digits.
  else if (hex.length === 4) {
    r = parseInt(hex.slice(1, 2).repeat(2), 16);
    g = parseInt(hex.slice(2, 3).repeat(2), 16);
    b = parseInt(hex.slice(3, 4).repeat(2), 16);
  }

  return isFormatted ? `rgb(${r},${g},${b})` : `${r},${g},${b}`;
};

export const getColouredIconUrl = (iconName: string, colour: string) => {
  const colourCode = colour.replace('#', '');
  return `https://hub-static.wayndata.com/icons/lastminute-icons-solid/${iconName}.svg?color=%23${colourCode}`;
};

export const getIsTransportEnabled = (productsData: ProductsData): boolean => !!productsData?.transport?.usecaseEnabled;

export const isNil = (v: unknown): boolean => v === undefined || v === null;
export const isNilString = (v: unknown): boolean => v === undefined || v === null || v === '';

export const isEmptyObject = (obj: any) => Object.keys(obj).length === 0 && obj.constructor === Object;

export const getIsFlex = (searchParams: SearchParams): boolean =>
  !!(
    searchParams[URLParameters.DATE_FROM] &&
    ((searchParams[URLParameters.DATE_TO]?.indexOf('A') ?? -1) !== -1 ||
      searchParams[URLParameters.DATE_FROM].indexOf('M') !== -1)
  );

export const createClickId = (searchId: string | undefined, userId: string | undefined): string =>
  `oss_${createPlatformSearchId(userId || '')}${searchId?.substring(0, 3)}`;

export const createPreClickIdWithNoTimestamp = (searchId: string | undefined, userId: string | undefined): string =>
  `${getUIDPrefixFourChars(userId || '')}${searchId?.substring(0, 3)}`;

export const createClickIdFromPreClickId = (preClickId: string): string => {
  if (preClickId && preClickId.length >= 7) {
    const uidPrefix = preClickId.slice(0, 4);
    const searchIdPart = preClickId.slice(4, 7);
    return `oss_${createPlatformSearchIdFromPreClickId(uidPrefix)}${searchIdPart}`;
  }
  return '';
};
